/**
 * Mic test — talk to the AI voice agent from the browser (SPEC-VOX-303).
 *
 * STANDARD engine — exercises the EXACT pipeline a phone call takes
 * (voice-loop TurnLoop: STT → agent → TTS, with barge-in and per-tenant
 * provider config), minus only the Twilio telephony leg. The browser
 * emulates the audio transport:
 *
 *   mic → getUserMedia → resample to 8 kHz → µ-law encode → WS "audio" frames
 *   WS "audio" frames → µ-law decode → Web Audio playback (the TTS voice)
 *   WS "transcript"/"metrics" events → live transcript pane + latency chips
 *
 * Auth: a 5-minute HMAC token minted by the backend (admin-only, tied to this
 * tenant) — the voice loop never trusts a client-supplied tenant id.
 *
 * ADVANCED engine — a real web call on the managed pipeline: the backend
 * mints a single-use access token (its own API key never reaches the
 * browser) and retell-client-js-sdk handles mic capture + playback itself.
 * Its transcript events are normalised into the same {speaker, text} shape,
 * so one render path serves both engines. The frame counters / latency chips
 * are Standard-only diagnostics — the SDK owns that transport.
 *
 * Browser requirements: HTTPS (or localhost) for getUserMedia; WSS when the
 * page is HTTPS.
 */
import { useEffect, useRef, useState } from 'react';
import { voiceProvidersService } from '../../lib/services';
import VoiceOrb from './VoiceOrb';
import './MicTestCard.css';

/* ───────────────────────── µ-law codec (G.711) ───────────────────────── */

const BIAS = 0x84;
const CLIP = 32635;

function pcm16ToMulaw(sample) {
  let sign = (sample >> 8) & 0x80;
  if (sign) sample = -sample;
  if (sample > CLIP) sample = CLIP;
  sample += BIAS;
  let exponent = 7;
  for (let mask = 0x4000; (sample & mask) === 0 && exponent > 0; mask >>= 1) exponent--;
  const mantissa = (sample >> (exponent + 3)) & 0x0f;
  return ~(sign | (exponent << 4) | mantissa) & 0xff;
}

function mulawToPcm16(ulaw) {
  ulaw = ~ulaw & 0xff;
  const sign = ulaw & 0x80;
  const exponent = (ulaw >> 4) & 0x07;
  const mantissa = ulaw & 0x0f;
  let sample = ((mantissa << 3) + BIAS) << exponent;
  sample -= BIAS;
  return sign ? -sample : sample;
}

function b64FromBytes(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(s);
}

function bytesFromB64(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/* ─────────────────────────────── the card ─────────────────────────────── */

const DEV_FALLBACK_WS = 'ws://localhost:8002/voice/mic-test';

export default function MicTestCard({ enabled, engineMode = 'STANDARD' }) {
  const advanced = engineMode === 'ADVANCED';
  const [state, setState] = useState('idle');       // idle | connecting | live | error
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);     // {stt, tts, source, voice_id} from "ready"
  const [transcript, setTranscript] = useState([]); // {speaker, text, confidence}
  const [metrics, setMetrics] = useState(null);     // last turn's latency
  const [io, setIo] = useState({ sent: 0, recv: 0, played: 0 }); // frames sent / acked / TTS frames played
  const [speaking, setSpeaking] = useState(false);  // true while TTS audio is actively playing
  const [beeped, setBeeped] = useState(false);      // speaker self-test fired at least once
  const [phone, setPhone] = useState('');           // ADVANCED-only, REQUIRED: the number this call runs as
  const [muted, setMuted] = useState(false);
  const [userTalking, setUserTalking] = useState(false);   // mic level above the speech threshold
  const [awaitingReply, setAwaitingReply] = useState(false); // user finished a turn, agent not yet speaking
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const refs = useRef({});                          // ws, ctx, stream, processor, playhead
  // {mic, ai} AnalyserNodes for the orb. A ref, so wiring them up mid-call
  // re-renders nothing; the orb reads them every animation frame.
  const analysers = useRef({ mic: null, ai: null });
  const mutedRef = useRef(false);                   // read inside the audio callback
  const chatRef = useRef(null);

  const cleanup = () => {
    const r = refs.current;
    try { clearTimeout(r.speakTimer); } catch { /* none */ }
    setSpeaking(false);
    try { r.retell?.stopCall(); } catch { /* already stopped */ }
    try { r.ws?.close(); } catch { /* already closed */ }
    try { r.processor?.disconnect(); } catch { /* detached */ }
    try { r.source?.disconnect(); } catch { /* detached */ }
    try { r.stream?.getTracks().forEach((t) => t.stop()); } catch { /* stopped */ }
    try { r.ctx?.close(); } catch { /* closed */ }
    // Advanced only: the meter's own mic tap, separate from the SDK's capture.
    try { r.micStream?.getTracks().forEach((t) => t.stop()); } catch { /* stopped */ }
    try { r.micCtx?.close(); } catch { /* closed */ }
    refs.current = {};
    analysers.current = { mic: null, ai: null };
    mutedRef.current = false;
    setMuted(false);
    setUserTalking(false);
    setAwaitingReply(false);
    setStartedAt(null);
    setElapsed(0);
  };

  useEffect(() => cleanup, []);                     // unmount safety

  const stop = () => {
    const r = refs.current;
    try { r.ws?.readyState === WebSocket.OPEN && r.ws.send(JSON.stringify({ type: 'stop' })); } catch { /* gone */ }
    cleanup();
    setState('idle');
    setSession(null);
  };

  /**
   * ADVANCED engine start: a real web call on the managed pipeline. The SDK
   * owns mic capture and audio playback, so none of the µ-law/resample path
   * below runs — we only wire its events into the same state the Standard
   * transcript pane renders.
   */
  const startAdvanced = async () => {
    setState('connecting'); setError(null); setTranscript([]); setMetrics(null);
    setIo({ sent: 0, recv: 0, played: 0 }); setSpeaking(false);
    try {
      // Lazy import: Standard-engine tenants never pay for the SDK bundle.
      const { RetellWebClient } = await import('retell-client-js-sdk');
      const client = new RetellWebClient();
      refs.current = { retell: client };

      client.on('call_started', () => {
        setSession({ stt: 'managed', tts: 'managed', source: 'advanced engine' });
        setState('live');
        setStartedAt(Date.now());
      });
      client.on('update', (update) => {
        // Retell sends the full running transcript: [{role, content}, ...].
        // Normalise into the shape the shared pane renders.
        if (Array.isArray(update?.transcript)) {
          setTranscript(update.transcript.slice(-40).map((t) => ({
            speaker: t.role === 'agent' ? 'ai' : 'user',
            text: t.content,
          })));
          // The caller has the floor until the agent answers; "thinking" is
          // shown once they also go quiet (see the phase below).
          if (update.transcript.at(-1)?.role === 'user') setAwaitingReply(true);
        }
      });
      client.on('agent_start_talking', () => { setSpeaking(true); setAwaitingReply(false); });
      client.on('agent_stop_talking', () => setSpeaking(false));
      client.on('call_ended', () => {
        if (refs.current.retell) { setState('idle'); setSession(null); }
        cleanup();
      });
      client.on('error', (err) => {
        setError(typeof err === 'string' ? err
          : (err?.message || 'The Advanced engine reported an error during the call.'));
        setState('error');
        cleanup();
      });

      const { accessToken } = await voiceProvidersService.webCallToken(phone.trim());
      // emitRawAudioSamples makes the SDK build an AnalyserNode on the agent's
      // audio (client.analyzerComponent) — that is what drives the orb while
      // the agent speaks. It is attached when the agent track arrives, so the
      // level loop below picks it up lazily.
      await client.startCall({ accessToken, emitRawAudioSamples: true });

      // The SDK keeps its microphone to itself, so the orb meters a second tap
      // on the same device. Best effort: without it the call still works, the
      // orb just doesn't move while you talk.
      try {
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const micCtx = new (window.AudioContext || window.webkitAudioContext)();
        const micAnalyser = micCtx.createAnalyser();
        micAnalyser.fftSize = 1024;
        micCtx.createMediaStreamSource(micStream).connect(micAnalyser);
        if (refs.current.retell === client) {
          refs.current = { ...refs.current, micStream, micCtx };
          analysers.current = { ...analysers.current, mic: micAnalyser };
        } else {
          // Call already ended while we were asking — don't leak the tap.
          micStream.getTracks().forEach((t) => t.stop());
          micCtx.close();
        }
      } catch { /* meter only */ }
    } catch (e) {
      setError(e?.name === 'NotAllowedError'
        ? 'Microphone access was denied — allow it in the browser and retry.'
        : (e?.response?.data?.message || e.message || 'Advanced-engine mic test failed to start.'));
      setState('error');
      cleanup();
    }
  };

  /**
   * Pre-flight the WebSocket URL and explain any problem in terms someone can act
   * on — the browser's own message ("An insecure WebSocket connection may not be
   * initiated from a page loaded over HTTPS") is technically accurate and tells
   * you nothing about which setting is wrong.
   *
   * Returns an error string, or null when the URL is usable.
   */
  const wsUrlProblem = (wsUrl) => {
    if (!wsUrl) {
      return 'The voice loop has no public address configured. Set VOICE_LOOP_STREAM_URL '
           + 'to the public wss:// origin of spectraiq-voice-loop (e.g. wss://dev.spectraiq.ai) '
           + 'and restart the backend.';
    }

    // The WebSocket constructor accepts http/https and coerces them to ws/wss, so an
    // http:// value fails as "insecure WebSocket" rather than "wrong scheme" — which
    // sends people looking in the wrong place entirely.
    const insecure = /^(ws|http):\/\//i.test(wsUrl);
    const pageIsHttps = window.location.protocol === 'https:';

    if (insecure && pageIsHttps) {
      return `The voice loop is configured as an insecure address (${wsUrl}). This page is `
           + 'served over HTTPS, so the browser refuses to open a plaintext WebSocket to it. '
           + 'Set VOICE_LOOP_STREAM_URL to a public wss:// origin (e.g. wss://dev.spectraiq.ai) '
           + 'and make sure your reverse proxy routes /voice/ to voice-loop:8002 with WebSocket '
           + 'upgrade headers — see infra/nginx/voice-loop.conf.';
    }

    // An internal Docker hostname is unreachable from a browser, and would also be
    // unreachable from Twilio. Catch it before the connection times out obscurely.
    if (/^(wss?|https?):\/\/(voice-loop|backend|localhost|127\.0\.0\.1)/i.test(wsUrl)
        && window.location.hostname !== 'localhost') {
      return `The voice loop address (${wsUrl}) is an internal or local hostname, which this `
           + 'browser cannot reach — and neither can Twilio. VOICE_LOOP_STREAM_URL must be the '
           + 'public wss:// origin of the voice loop.';
    }

    return null;
  };

  const start = async () => {
    if (advanced) return startAdvanced();
    setState('connecting'); setError(null); setTranscript([]); setMetrics(null);
    setIo({ sent: 0, recv: 0, played: 0 }); setSpeaking(false);
    try {
      const { token, wsUrl } = await voiceProvidersService.micTestToken();

      // Fail with a sentence that names the setting, before the browser fails with
      // one that doesn't.
      const base = wsUrl || DEV_FALLBACK_WS;
      const problem = wsUrlProblem(wsUrl);
      if (problem) {
        setError(problem);
        setState('error');
        return;
      }

      const url = `${base}?token=${encodeURIComponent(token)}`;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Browsers can hand back a *suspended* context even inside a click handler
      // (the getUserMedia await can consume the user-gesture activation). A
      // suspended context accepts buffers but plays nothing — the classic
      // "transcript shows but no voice" symptom. Resume it explicitly.
      if (ctx.state === 'suspended') { try { await ctx.resume(); } catch { /* best effort */ } }
      const source = ctx.createMediaStreamSource(stream);
      // ScriptProcessor: universally supported; 4096 samples ≈ 85 ms per push.
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      const inRate = ctx.sampleRate;                // typically 44100/48000
      const ratio = inRate / 8000;

      // Meters for the orb: one on the mic, one in front of the speakers that
      // every AI audio buffer is routed through.
      const micAnalyser = ctx.createAnalyser();
      micAnalyser.fftSize = 1024;
      source.connect(micAnalyser);
      const aiAnalyser = ctx.createAnalyser();
      aiAnalyser.fftSize = 1024;
      aiAnalyser.connect(ctx.destination);
      analysers.current = { mic: micAnalyser, ai: aiAnalyser };

      const ws = new WebSocket(url);
      refs.current = { ws, ctx, stream, source, processor, aiAnalyser, playhead: 0, readPos: 0, sources: new Set() };

      ws.onopen = () => {
        source.connect(processor);
        processor.connect(ctx.destination);        // required by some browsers; emits silence
        let carry = new Float32Array(0);
        processor.onaudioprocess = (e) => {
          if (ws.readyState !== WebSocket.OPEN) return;
          const input = e.inputBuffer.getChannelData(0);
          const data = new Float32Array(carry.length + input.length);
          data.set(carry); data.set(input, carry.length);
          const outLen = Math.floor(data.length / ratio);
          const ulaw = new Uint8Array(outLen);
          for (let i = 0; i < outLen; i++) {
            // linear-interpolated resample to 8 kHz
            const pos = i * ratio;
            const i0 = Math.floor(pos);
            const frac = pos - i0;
            const s = data[i0] * (1 - frac) + (data[i0 + 1] ?? data[i0]) * frac;
            // Muted sends silence rather than nothing, like a phone's mute
            // button: the line stays open and the no-input timer still runs.
            const pcm = mutedRef.current ? 0 : Math.max(-1, Math.min(1, s)) * 0x7fff;
            ulaw[i] = pcm16ToMulaw(pcm | 0);
          }
          carry = data.subarray(Math.floor(outLen * ratio));
          if (outLen > 0) {
            ws.send(JSON.stringify({ type: 'audio', payload: b64FromBytes(ulaw) }));
            setIo((p) => ({ ...p, sent: p.sent + 1 }));
          }
        };
      };

      ws.onmessage = (msg) => {
        let ev;
        try { ev = JSON.parse(msg.data); } catch { return; }
        if (ev.type === 'ready') {
          setSession(ev);
          setState('live');
          setStartedAt(Date.now());
        } else if (ev.type === 'audio') {
          setAwaitingReply(false);
          // A suspended context here would silently drop the AI's voice — resume
          // on the first frame too, in case the gesture lapsed after start().
          if (ctx.state === 'suspended') ctx.resume().catch(() => {});
          const ulaw = bytesFromB64(ev.payload);
          const pcm = new Float32Array(ulaw.length);
          for (let i = 0; i < ulaw.length; i++) pcm[i] = mulawToPcm16(ulaw[i]) / 0x8000;
          const buf = ctx.createBuffer(1, pcm.length, 8000);
          buf.getChannelData(0).set(pcm);
          const node = ctx.createBufferSource();
          node.buffer = buf;
          node.connect(refs.current.aiAnalyser || ctx.destination);
          const at = Math.max(ctx.currentTime, refs.current.playhead || 0);
          node.start(at);
          refs.current.playhead = at + buf.duration;
          // Track scheduled nodes so a server 'clear' (barge-in) can stop them.
          refs.current.sources?.add(node);
          node.onended = () => refs.current.sources?.delete(node);
          // Count what actually reached the speakers, and light the "speaking"
          // indicator until the scheduled audio finishes — so you can SEE the
          // TTS voice arriving even before you (hopefully) hear it.
          setIo((p) => ({ ...p, played: p.played + 1 }));
          setSpeaking(true);
          clearTimeout(refs.current.speakTimer);
          refs.current.speakTimer = setTimeout(
            () => setSpeaking(false),
            Math.max(150, (refs.current.playhead - ctx.currentTime) * 1000 + 120),
          );
        } else if (ev.type === 'clear') {
          // Barge-in committed: the server cut the AI off. Audio frames arrive
          // far ahead of real time, so seconds of reply are queued locally —
          // stop them all, exactly like the Twilio `clear` a phone call gets.
          for (const n of refs.current.sources || []) {
            try { n.stop(); } catch { /* already finished */ }
          }
          refs.current.sources?.clear();
          refs.current.playhead = 0;
          clearTimeout(refs.current.speakTimer);
          setSpeaking(false);
        } else if (ev.type === 'transcript') {
          setTranscript((t) => [...t.slice(-40), ev]);
          // The voice loop sends the caller's line once they finish speaking,
          // so from here until the first audio frame the agent is thinking.
          setAwaitingReply(ev.speaker !== 'ai');
        } else if (ev.type === 'stats') {
          // Proof the server is receiving our audio. If this climbs but no
          // transcript ever appears, the problem is downstream of the socket (STT).
          setIo((p) => ({ ...p, recv: ev.frames_in ?? p.recv }));
        } else if (ev.type === 'metrics') {
          setMetrics(ev);
        } else if (ev.type === 'error') {
          setError(ev.message);
        }
      };

      ws.onclose = (e) => {
        if (e.code === 4401) {
          setError('Mic test rejected: token invalid or expired (is the shared secret configured on the voice loop?)');
          setState('error');
        } else if (refs.current.ws) {
          setState('idle');
        }
        cleanup();
      };
      ws.onerror = () => {
        setError('Could not reach the voice loop — check it is running and the WS URL is reachable from your browser.');
        setState('error');
        cleanup();
      };
    } catch (e) {
      setError(e?.name === 'NotAllowedError'
        ? 'Microphone access was denied — allow it in the browser and retry.'
        : (e?.response?.data?.message || e.message || 'Mic test failed to start.'));
      setState('error');
      cleanup();
    }
  };

  const live = state === 'live';

  // Is the caller talking right now? Sampled at 10 Hz with hysteresis: on at
  // once above the threshold, off only after ~500 ms of quiet, so the status
  // doesn't flicker between words.
  useEffect(() => {
    if (!live) return undefined;
    const buf = new Uint8Array(512);
    let quietSince = 0;
    let talking = false;
    const id = setInterval(() => {
      const r = refs.current;
      // Advanced: the SDK attaches its agent-audio analyser after the call starts.
      if (!analysers.current.ai && r.retell?.analyzerComponent?.analyser) {
        analysers.current = { ...analysers.current, ai: r.retell.analyzerComponent.analyser };
      }
      const mic = analysers.current.mic;
      let level = 0;
      if (mic && !mutedRef.current) {
        mic.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        level = Math.sqrt(sum / buf.length);
      }
      const now = Date.now();
      if (level > 0.035) {
        quietSince = 0;
        if (!talking) { talking = true; setUserTalking(true); }
      } else if (talking) {
        if (!quietSince) quietSince = now;
        if (now - quietSince > 500) { talking = false; setUserTalking(false); }
      }
    }, 100);
    return () => clearInterval(id);
  }, [live]);

  // Call timer.
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  // Keep the newest line in view.
  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript, awaitingReply]);

  const toggleMute = () => {
    const next = !muted;
    mutedRef.current = next;
    setMuted(next);
    const client = refs.current.retell;
    if (client) { try { next ? client.mute() : client.unmute(); } catch { /* not connected */ } }
  };

  const canStart = enabled && state !== 'connecting' && (!advanced || phone.trim());

  const phase = state === 'connecting' ? 'connecting'
    : state === 'error' ? 'error'
    : !live ? 'idle'
    : speaking ? 'speaking'
    : userTalking && !muted ? 'listening'
    : awaitingReply ? 'thinking'
    : 'listening';

  const status = {
    idle: {
      title: 'Ready when you are',
      sub: !enabled
        ? (advanced ? 'Save your Advanced engine API key and agent ID first.' : 'Save and verify your providers first.')
        : advanced && !phone.trim()
          ? 'Enter the phone number this test call runs as, then tap the orb.'
          : 'Tap the orb to start a live call with your AI agent.',
    },
    connecting: { title: 'Connecting…', sub: 'Setting up a live call with your agent.' },
    listening: muted
      ? { title: 'You’re muted', sub: 'Unmute to talk to the agent.' }
      : userTalking
        ? { title: 'Listening…', sub: 'Go ahead — the agent can hear you.' }
        : { title: 'Your turn', sub: 'Say something to the agent.' },
    thinking: { title: 'Thinking…', sub: 'The agent is working on its reply.' },
    speaking: { title: 'Speaking', sub: 'The agent is talking — you can interrupt.' },
    error: { title: 'The test couldn’t run', sub: 'See the details below, then try again.' },
  }[phase];

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;

  // Speaker self-test: play a 440 Hz beep through the SAME Web Audio path the AI
  // voice uses. It decisively splits "I hear nothing" into two cases — if you
  // can't hear this beep, it's your system output device / volume / muted tab;
  // if you CAN hear it but not the AI, the fault is in the audio the pipeline
  // sends (decode/scheduling), not your speakers.
  const testSpeaker = async () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') await ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 440;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.55);
      setBeeped(true);
      setTimeout(() => { try { ctx.close(); } catch { /* closed */ } }, 900);
    } catch {
      setBeeped(false);
    }
  };

  // Actionable problems — shown up front, not hidden in diagnostics.
  const warnings = [];
  if (live && !advanced) {
    if (transcript.some((t) => t.speaker === 'ai') && io.played === 0) {
      warnings.push('The AI replied in text but sent no audio — the TTS provider produced nothing. '
        + 'Check the TTS API key in Settings → Voice agents (for Deepgram, the same key as STT), '
        + 'and look for an error below.');
    }
    if (io.sent > 20 && io.recv === 0) {
      warnings.push('The server isn’t acknowledging your audio — the socket is open but frames aren’t '
        + 'landing. Check the /voice/ proxy route (WebSocket upgrade + no buffering).');
    }
    if (io.recv > 20 && transcript.length === 0) {
      warnings.push('The server is receiving audio but the speech-to-text provider has produced nothing. '
        + 'Check the STT API key in Settings → Voice agents, and that you’re actually speaking.');
    }
  }

  const orbAction = live ? 'End the test call' : 'Start the test call';

  return (
    <div className="card animate-in" style={{ padding: 16 }}>
      <div className="mt-header">
        <div>
          <div className="card-label">MIC TEST</div>
          <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
            {advanced
              ? 'Talk to your AI agent through your microphone — a real web call on the Advanced engine.'
              : 'Talk to your AI agent through your microphone — the same STT → agent → TTS pipeline as a real call.'}
          </div>
        </div>
        <button className="btn small" onClick={testSpeaker} title="Play a test tone through your speakers">
          🔈 Test speaker
        </button>
      </div>

      <div className="mt-stage">
        <button
          type="button"
          className="mt-orb"
          onClick={live ? stop : start}
          disabled={state === 'connecting' || (!live && !canStart)}
          aria-label={orbAction}
          title={live ? orbAction : (canStart ? orbAction : status.sub)}
        >
          <VoiceOrb phase={phase} analysers={analysers} muted={muted} />
        </button>

        <div className="mt-status-title" aria-live="polite">{status.title}</div>
        <div className="mt-status-sub">{status.sub}</div>

        {live && (
          <div className="mt-timer">
            <span className="mt-live-dot" aria-hidden="true" /> LIVE · {mmss}
          </div>
        )}

        <div className="mt-controls">
          {!live && advanced && (
            <input
              className="mt-phone"
              placeholder="Phone number *"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && canStart) start(); }}
              disabled={state === 'connecting'}
              required
              aria-required="true"
              aria-label="Phone number for this test call"
              title="Required. The number this test call runs as — the agent passes it to the booking tools as customer_phone."
            />
          )}
          {live ? (
            <>
              <button className={`btn small ${muted ? 'primary' : ''}`} onClick={toggleMute} aria-pressed={muted}>
                {muted ? '🎙 Unmute' : '🔇 Mute'}
              </button>
              <button className="btn small danger" onClick={stop}>■ End call</button>
            </>
          ) : (
            // The phone input isn't inside a <form>, so `required` alone blocks
            // nothing — the disabled button (and orb) is what enforces it.
            <button
              className="btn small primary"
              disabled={!canStart}
              title={advanced && !phone.trim() ? 'Enter the phone number for this test call first' : undefined}
              onClick={start}
            >
              {state === 'connecting' ? 'Connecting…' : state === 'error' ? '↻ Try again' : '● Start mic test'}
            </button>
          )}
        </div>
      </div>

      {(transcript.length > 0 || live) && (
        <div className="mt-chat" ref={chatRef}>
          {transcript.length === 0 && (
            <div className="mt-chat-empty">
              {advanced
                ? 'The conversation appears here as you talk…'
                : 'Say something — your words appear here as speech-to-text hears them…'}
            </div>
          )}
          {transcript.map((t, i) => (
            <div key={i} className={`mt-msg ${t.speaker === 'ai' ? 'ai' : 'user'}`}>
              <div className="mt-msg-who">{t.speaker === 'ai' ? 'Agent' : 'You'}</div>
              <div className="mt-bubble">{t.text}</div>
              {t.speaker !== 'ai' && t.confidence != null && t.confidence > 0 && (
                <div className="mt-conf">heard with {(t.confidence * 100).toFixed(0)}% confidence</div>
              )}
            </div>
          ))}
          {phase === 'thinking' && (
            <div className="mt-msg ai" aria-label="Agent is thinking">
              <div className="mt-msg-who">Agent</div>
              <div className="mt-bubble mt-typing"><span /><span /><span /></div>
            </div>
          )}
        </div>
      )}

      {warnings.map((w) => <div key={w} className="mt-warning">{w}</div>)}
      {error && <div className="mt-error">{error}</div>}

      {beeped && (
        <div className="mono text-xs text-muted" style={{ marginBottom: 8 }}>
          Played a test tone. Heard it? Your speakers are fine — if the AI is still silent the
          problem is the pipeline audio. Didn’t hear it? Fix your system output device / volume
          (and check the tab isn’t muted), then the AI voice will come through too.
        </div>
      )}

      {live && (
        <div className="mono text-xs text-muted" style={{ lineHeight: 1.6, textAlign: 'center' }}>
          Use headphones — echo cancellation is on, but speakers can still trigger barge-in.
          {!advanced && ' Sessions time out with the 5-minute token; just start again.'}
        </div>
      )}

      {(session || metrics) && (
        <details className="mt-diag">
          <summary>Diagnostics</summary>
          <div className="mt-diag-body">
            {session && (
              <div className="flex-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <span className="pill accent filled">STT · {session.stt}</span>
                <span className="pill accent filled">TTS · {session.tts}</span>
                <span className="pill">config · {session.source}</span>
                {session.barge_in !== undefined && (
                  <span className={`pill ${session.barge_in ? 'warning filled' : 'success filled'}`}>
                    barge-in {session.barge_in ? 'ON' : 'OFF'}
                    {session.barge_in_source ? ` (${session.barge_in_source})` : ''}
                  </span>
                )}
              </div>
            )}

            {metrics && (
              <div className="flex-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <span className={`pill ${metrics.total_ms <= 1200 ? 'success' : metrics.total_ms <= 1500 ? 'warning' : 'danger'} filled`}>
                  turn {metrics.total_ms} ms
                </span>
                {metrics.agent_ms != null && <span className="pill">agent {metrics.agent_ms} ms</span>}
                {metrics.tts_ms != null && <span className="pill">tts {metrics.tts_ms} ms</span>}
                {metrics.barge_in && <span className="pill warning filled">barge-in</span>}
              </div>
            )}

            {/* Pipeline telemetry — STANDARD engine only; the Advanced SDK owns
                its transport, so frame counters would always read zero there.
                - sent climbs, received 0        → the server isn't getting your audio (proxy/socket)
                - sent & received climb, no text → STT is silent (key / format / no speech detected)
                - transcript appears, no audio   → agent or TTS (an error event will also show) */}
            {live && !advanced && (
              <div className="mono text-xs" style={{ lineHeight: 1.7 }}>
                audio sent · {io.sent} frames &nbsp;·&nbsp; server received · {io.recv} frames
                &nbsp;·&nbsp; AI voice played · {io.played} frames
                {io.played > 0 && (
                  <div style={{ marginTop: 3 }}>
                    Voice audio is arriving and playing. If you can’t hear it, check your system
                    output device/volume (use headphones to avoid barge-in from the speakers).
                  </div>
                )}
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}
